import { PaginatedResponseWithCountDto, ApiError } from "@/client/extend";
import {
  DataTableContext,
  PaginationRequest,
} from "@/components/custom/data-table/data-table-context";
import { QueryKey } from "@tanstack/react-query";
import { useMemo } from "react";
import { FieldPath } from "react-hook-form";

type QueryArgument = PaginationRequest & {
  orderBy?: any;
  sortBy?: any;
  id?: number;
};

export function stripPaginationFields<T extends QueryArgument>(
  query: T,
): Omit<T, "limit" | "page" | "orderBy" | "sortBy" | "id"> {
  const { limit, page, orderBy, sortBy, id, ...filterFields } = query;
  return filterFields;
}

// Always returns the table's current filter/search/sort state, regardless of row selection —
// unlike useFinalQuery, which returns only the selected IDs when a partial selection is active.
export function useTableFilterQuery<
  TRow = unknown,
  TArgument extends QueryArgument = QueryArgument,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
>(
  context: DataTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >,
) {
  const [query] = context.useTableQueryArgument();
  return useMemo(() => stripPaginationFields(query), [query]);
}

type IndexType<T> = T extends Array<infer K> ? K : never;

type UseFinalQueryProps<
  K extends keyof TArgument,
  TRow = unknown,
  TArgument extends QueryArgument = QueryArgument,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
> = {
  idsKey: K;
  context: DataTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >;
  booleanKey?: keyof TArgument;
  convert?: (id: string) => IndexType<TArgument[K]>;
} & (IndexType<TArgument[K]> extends string
  ? {}
  : {
      convert: (id: string) => IndexType<TArgument[K]>;
    });

function hasBooleanKey<
  K extends keyof TArgument,
  TRow = unknown,
  TArgument extends QueryArgument = QueryArgument,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
>(
  props: UseFinalQueryProps<
    K,
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >,
) {
  return "booleanKey" in props && props.booleanKey !== undefined;
}

export const useFinalQuery = <
  K extends keyof TArgument,
  TRow = unknown,
  TArgument extends QueryArgument = QueryArgument,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
>(
  props: UseFinalQueryProps<
    K,
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >,
): {
  finalQuery: TArgument;
  count: number;
  allEnabledStatus: boolean;
} => {
  const [query] = props.context.useTableQueryArgument();
  const isAllSelected = props.context.useTable().getAllSelected();

  const rowsSelection = props.context.useTable().getState().rowSelection;
  const total = props.context.useTableTotal().value || 0;

  return useMemo(() => {
    let finalQuery: TArgument = {} as TArgument;
    let count = 0;
    let allEnabledStatus = false;

    const filterFields = stripPaginationFields(query);
    // Check for enabled status in filterFields
    if (hasBooleanKey(props)) {
      const booleanValue = (filterFields as any)[props.booleanKey];
      allEnabledStatus = Boolean(booleanValue);
    }

    if (!isAllSelected) {
      const elementIds = Object.entries(rowsSelection)
        .filter(([, value]) => value)
        .map(([key]) => (props?.convert ? props.convert(key) : key));
      count = elementIds.length;
      finalQuery[props.idsKey] = elementIds as TArgument[K];
    } else {
      finalQuery = { ...filterFields } as TArgument;
      count = total;
    }

    return {
      finalQuery,
      count,
      allEnabledStatus,
    };
  }, [query, isAllSelected, rowsSelection]);
};
